import { LanguageToggle, useT } from '@/lib/i18n';
import { PageLoader } from '@/components/ui/loader';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, ChevronRight, Search, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Markdown } from '@/lib/markdown';
import { apiGet, apiPost } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { visitorKey } from './public-form';

type Home = {
  name: string;
  company: string;
  colour: string;
  trackingKey: string;
  categories: { id: string; name: string; slug: string; description: string | null; articleCount: number; articles: { id: string; title: string; slug: string }[] }[];
  popular: { id: string; title: string; slug: string; excerpt: string | null }[];
};

type ArticleView = {
  id: string;
  title: string;
  body: string;
  updatedAt: string;
  category: { name: string; slug: string } | null;
  related: { title: string; slug: string }[];
  center: { name: string; colour: string; trackingKey: string };
};

function useCenter(slug: string) {
  return useQuery({ queryKey: ['help', slug], queryFn: () => apiGet<Home>(`/public/help/${slug}`), retry: false, staleTime: 60_000 });
}

function track(key: string | undefined, title: string) {
  if (!key) return;
  void apiPost(`/public/site/${key}/collect`, { visitorKey: visitorKey(), url: window.location.href, title, referrer: document.referrer || null }).catch(() => undefined);
}

function Shell({ slug, name, colour, children, search = true }: { slug: string; name: string; colour: string; children: React.ReactNode; search?: boolean }) {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const t = useT();
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ colorScheme: 'light' }}>
      <header className="relative overflow-hidden px-5 pb-14 pt-10 text-white" style={{ background: `linear-gradient(120deg, ${colour}, #0f172a)` }}>
        <div className="mx-auto max-w-4xl">
          <Link to={`/help/${slug}`} className="flex items-center gap-2 text-lg font-bold">
            <BookOpen className="h-5 w-5" /> {name}
          </Link>
          <LanguageToggle tone="dark" className="float-end -mt-8" />
          {search && (
            <form
              className="mt-8"
              onSubmit={(event) => {
                event.preventDefault();
                window.location.assign(`/help/${slug}/search?q=${encodeURIComponent(q)}`);
              }}
            >
              <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{t('How can we help?')}</h1>
              <div className="mt-5 flex items-center gap-2 rounded-2xl bg-white p-2 shadow-xl">
                <Search className="ms-2 h-5 w-5 text-slate-400" />
                <input
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
                  placeholder={t('Search articles…')}
                  className="min-w-0 flex-1 bg-transparent px-1 py-2 text-slate-900 outline-none"
                />
                <button className="rounded-xl px-4 py-2 font-semibold text-white" style={{ backgroundColor: colour }}>
                  {t('Search')}
                </button>
              </div>
            </form>
          )}
        </div>
      </header>
      <main className="relative mx-auto -mt-6 max-w-4xl px-5 pb-16">{children}</main>
    </div>
  );
}

const NotFound = () => <div className="flex min-h-screen items-center justify-center text-slate-600">This help centre is not available.</div>;

/** dovocrm.com/help/<slug> */
export function HelpHomePage() {
  const { slug = '' } = useParams();
  const { data, isLoading, isError } = useCenter(slug);
  useEffect(() => {
    if (data) {
      document.title = data.name;
      track(data.trackingKey, data.name);
    }
  }, [data]);
  if (isLoading) return <PageLoader />;
  if (isError || !data) return <NotFound />;

  return (
    <Shell slug={slug} name={data.name} colour={data.colour}>
      <div className="grid gap-4 sm:grid-cols-2">
        {data.categories.map((category) => (
          <div key={category.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <Link to={`/help/${slug}/c/${category.slug}`} className="text-lg font-bold hover:underline">
              {category.name}
            </Link>
            {category.description && <p className="mt-1 text-sm text-slate-500">{category.description}</p>}
            <ul className="mt-3 space-y-1.5">
              {category.articles.map((article) => (
                <li key={article.id}>
                  <Link to={`/help/${slug}/${article.slug}`} className="flex items-center gap-1 text-sm hover:underline" style={{ color: data.colour }}>
                    <ChevronRight className="h-3.5 w-3.5" /> {article.title}
                  </Link>
                </li>
              ))}
            </ul>
            {category.articleCount > category.articles.length && (
              <Link to={`/help/${slug}/c/${category.slug}`} className="mt-3 inline-block text-xs font-semibold text-slate-500 hover:underline">
                All {category.articleCount} articles
              </Link>
            )}
          </div>
        ))}
      </div>
      {data.categories.length === 0 && <p className="rounded-2xl bg-white p-10 text-center text-slate-500 shadow-sm">No articles yet.</p>}
    </Shell>
  );
}

/** /help/<slug>/c/<category> and /help/<slug>/search?q= */
export function HelpListPage() {
  const { slug = '', category } = useParams();
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';
  const center = useCenter(slug);
  const { data } = useQuery({
    queryKey: ['help', slug, 'list', category, q],
    queryFn: () =>
      apiGet<{ category: { name: string; description: string | null } | null; articles: { id: string; title: string; slug: string; excerpt: string | null }[] }>(
        `/public/help/${slug}/articles`,
        { params: { ...(q ? { q } : {}), ...(category ? { category } : {}) } },
      ),
    enabled: center.isSuccess,
  });
  if (center.isError) return <NotFound />;
  if (!center.data) return <PageLoader />;

  return (
    <Shell slug={slug} name={center.data.name} colour={center.data.colour}>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <Link to={`/help/${slug}`} className="flex items-center gap-1 text-sm text-slate-500 hover:underline">
          <ArrowLeft className="h-4 w-4" /> All topics
        </Link>
        <h2 className="mt-3 text-2xl font-extrabold">{category ? data?.category?.name : `Results for “${q}”`}</h2>
        {data?.category?.description && <p className="mt-1 text-slate-500">{data.category.description}</p>}
        <ul className="mt-5 divide-y">
          {(data?.articles ?? []).map((article) => (
            <li key={article.id} className="py-3">
              <Link to={`/help/${slug}/${article.slug}`} className="font-semibold hover:underline" style={{ color: center.data.colour }}>
                {article.title}
              </Link>
              {article.excerpt && <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">{article.excerpt}</p>}
            </li>
          ))}
        </ul>
        {data && !data.articles.length && <p className="py-6 text-center text-slate-500">Nothing found. Try other words.</p>}
      </div>
    </Shell>
  );
}

/** /help/<slug>/<article> */
export function HelpArticlePage() {
  const { slug = '', article = '' } = useParams();
  const [voted, setVoted] = useState<boolean | null>(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['help', slug, 'article', article],
    queryFn: () => apiGet<ArticleView>(`/public/help/${slug}/articles/${article}`),
    retry: false,
  });
  const vote = useMutation({ mutationFn: (helpful: boolean) => apiPost(`/public/help/${slug}/articles/${data?.id}/feedback`, { helpful }) });
  useEffect(() => {
    if (data) {
      document.title = `${data.title} — ${data.center.name}`;
      track(data.center.trackingKey, data.title);
    }
  }, [data]);
  if (isLoading) return <PageLoader />;
  if (isError || !data) return <NotFound />;

  return (
    <Shell slug={slug} name={data.center.name} colour={data.center.colour} search={false}>
      <article className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-slate-500">
          <Link to={`/help/${slug}`} className="hover:underline">
            Help centre
          </Link>
          {data.category && (
            <>
              <ChevronRight className="h-3.5 w-3.5" />
              <Link to={`/help/${slug}/c/${data.category.slug}`} className="hover:underline">
                {data.category.name}
              </Link>
            </>
          )}
        </nav>
        <h1 className="mt-4 text-3xl font-extrabold tracking-tight">{data.title}</h1>
        <p className="mt-1 text-xs text-slate-400">Updated {formatDate(data.updatedAt)}</p>
        <Markdown source={data.body} className="mt-6 text-[1.02rem] text-slate-700" />
        <div className="mt-10 flex flex-wrap items-center gap-3 border-t pt-6">
          {voted === null ? (
            <>
              <span className="text-sm font-medium">Was this helpful?</span>
              {[true, false].map((helpful) => (
                <button
                  key={String(helpful)}
                  onClick={() => {
                    setVoted(helpful);
                    vote.mutate(helpful);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm hover:bg-slate-50"
                >
                  {helpful ? <ThumbsUp className="h-4 w-4" /> : <ThumbsDown className="h-4 w-4" />} {helpful ? 'Yes' : 'No'}
                </button>
              ))}
            </>
          ) : (
            <span className="text-sm text-slate-500">Thanks for letting us know.</span>
          )}
        </div>
        {data.related.length > 0 && (
          <div className="mt-8">
            <p className="text-sm font-bold">Related articles</p>
            <ul className="mt-2 space-y-1">
              {data.related.map((item) => (
                <li key={item.slug}>
                  <Link to={`/help/${slug}/${item.slug}`} className="text-sm hover:underline" style={{ color: data.center.colour }}>
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </article>
    </Shell>
  );
}
